import React, { useState } from "react";
import {
  Button,
  Container,
  TextField,
  Typography,
} from "@material-ui/core";
import { Link as RouterLink, useHistory } from "react-router-dom";

import Header from "../../components/Header/Header";
import Footer from "../../components/Footer/Footer";

interface LoginProps {
  cartCount?: number;
  onCartClick?: () => void;
}

const Login: React.FC<LoginProps> = ({
  cartCount = 0,
  onCartClick = () => {},
}) => {
  const history = useHistory();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    // Clear previous error
    setError("");

    // Basic validation
    if (!email.trim() || !password.trim()) {
      setError("Email and password are required");
      return;
    }

    try {
      setLoading(true);

      const payload = JSON.stringify({
        email: email.trim(),
        password,
        business: "69f76a635d99871fd9349c1e",
      });

      const response = await fetch(
        "https://shopify.anainfo.ai/api/users/login",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: payload,
        }
      );

      if (!response.ok) {
        const errorText = await response.text();

        let errorMessage = "Invalid email or password";

        try {
          const errorData = JSON.parse(errorText);

          errorMessage =
            errorData?.message ||
            errorData?.error ||
            errorData?.errors ||
            errorMessage;
        } catch {
          if (errorText) {
            errorMessage = errorText;
          }
        }

        throw new Error(errorMessage);
      }

      const data = await response.json();

      console.log("Login response:", data);

      // Save token
      if (data?.token) {
        localStorage.setItem("token", data.token);
      } else {
        throw new Error("Login successful, but no token was received.");
      }

      // Login successful
      history.push("/shop");
    } catch (err: any) {
      console.error("Login error:", err);

      setError(
        err?.message || "Login failed. Please check your credentials."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <Header
        cartCount={cartCount}
        onCartClick={onCartClick}
      />

      <section className="auth-section">
        <Container maxWidth="lg">
          <div className="auth-wrapper">

            {/* =========================
                LOGIN FORM
            ========================= */}

            <div className="auth-form-container">

              <Typography className="auth-eyebrow">
                Welcome Back
              </Typography>

              <Typography className="auth-title">
                Login to Your Account
              </Typography>

              <Typography className="auth-description">
                Welcome back to Organic Shop. Please enter
                your details to continue.
              </Typography>

              <form
                className="auth-form"
                onSubmit={handleSubmit}
              >

                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Enter your email"
                  type="email"
                  value={email}
                  onChange={(event) =>
                    setEmail(event.target.value)
                  }
                  className="auth-input"
                  disabled={loading}
                  required
                />

                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Enter your password"
                  type="password"
                  value={password}
                  onChange={(event) =>
                    setPassword(event.target.value)
                  }
                  className="auth-input"
                  disabled={loading}
                  required
                />

                {/* API ERROR */}
                {error && (
                  <Typography
                    color="error"
                    variant="body2"
                    style={{
                      marginTop: 8,
                      marginBottom: 8,
                    }}
                  >
                    {error}
                  </Typography>
                )}

                <div className="auth-options">

                  <label className="remember-me">
                    <input type="checkbox" />
                    <span>Remember me</span>
                  </label>

                  <Button
                    className="forgot-password"
                    type="button"
                    disabled={loading}
                  >
                    Forgot Password?
                  </Button>

                </div>

                <Button
                  type="submit"
                  fullWidth
                  className="auth-submit-button"
                  disabled={loading}
                >
                  {loading ? "LOGGING IN..." : "LOGIN"}
                </Button>

              </form>

              <div className="auth-switch">

                <Typography>
                  Don't have an account?
                </Typography>

                <Button
                  component={RouterLink}
                  to="/signup"
                  className="auth-link-button"
                  disabled={loading}
                >
                  Create Account
                </Button>

              </div>

            </div>

            {/* =========================
                GREEN SIDE CARD
            ========================= */}

            <div className="auth-side-card">

              <Typography className="auth-side-eyebrow">
                100% Natural
              </Typography>

              <Typography className="auth-side-title">
                Welcome to
                <br />
                Organic Shop
              </Typography>

              <div className="auth-side-divider" />

              <Typography className="auth-side-text">
                Fresh organic products, healthy choices
                and better living. Join our community
                and discover something naturally good.
              </Typography>

              <Button
                component={RouterLink}
                to="/signup"
                className="auth-side-button"
                disabled={loading}
              >
                CREATE ACCOUNT
              </Button>

            </div>

          </div>
        </Container>
      </section>

      <Footer />
    </div>
  );
};

export default Login;